/**
 * Job tier classification utilities (job size → Triage Credit burn: 1 / 3 / 10).
 * Matches backend tier categorization logic
 */

/** Global flat vendor search fee (USD) — checkout always uses this amount. */
export const CLUMSE_SEARCH_FEE_USD = 49;

export const PROCUREMENT_TIERS = {
  MINOR: {
    label: 'Service',
    fee: 49,
    categories: [
      'handyman',
      'plumbing_minor', 
      'electrical_minor',
      'appliance_repair',
      'fixture_replacement',
      'door_repair',
      'window_repair', 
      'drywall_patch',
      'paint_touch_up',
      'cabinet_repair'
    ],
    keywords: [
      'fix', 'repair', 'replace', 'broken', 'leaky', 'stuck', 'loose',
      'faucet', 'handle', 'switch', 'outlet', 'light', 'door', 'window',
      'patch', 'hole', 'crack', 'paint', 'touch up', 'appliance',
      'dishwasher', 'garbage disposal', 'toilet', 'sink', 'cabinet'
    ],
    description: 'Basic repairs and maintenance tasks'
  },
  MAJOR: {
    label: 'Major System',
    fee: 49,
    categories: [
      'hvac',
      'water_heater',
      'roof_repair',
      'main_line',
      'electrical_panel',
      'plumbing_major',
      'flooring',
      'window_replacement',
      'door_replacement',
      'deck_repair',
      'fence_repair'
    ],
    keywords: [
      'hvac', 'ac', 'air conditioning', 'furnace', 'heater', 'heating',
      'water heater', 'roof', 'leak', 'main line', 'sewer', 'electrical panel',
      'breaker', 'flooring', 'floor', 'carpet', 'hardwood', 'tile',
      'replace window', 'replace door', 'deck', 'fence', 'major plumbing'
    ],
    description: 'Major system repairs and replacements'
  },
  CAPEX: {
    label: 'Large project',
    fee: 49,
    categories: [
      'renovation',
      'roof_replacement',
      'unit_turn',
      'kitchen_remodel',
      'bathroom_remodel',
      'basement_finish',
      'addition',
      'major_electrical',
      'major_plumbing',
      'structural'
    ],
    keywords: [
      'remodel', 'renovation', 'gut', 'addition', 'extension',
      'kitchen remodel', 'bathroom remodel', 'basement', 'finish',
      'new roof', 'roof replacement', 'unit turn', 'turnover',
      'structural', 'foundation', 'major renovation'
    ],
    description: 'Large renovations, installs, and structural work'
  }
};

/**
 * Classify job tier based on TCS data or fallback to conversation text
 * @param {Object} options - Classification options
 * @param {string} [options.conversationText] - Full conversation text for keyword fallback
 * @param {number} [options.tcs] - Technical Confidence Score (1-100)
 * @param {string} [options.suggestedTier] - AI-suggested tier from TCS engine
 * @param {string} [options.primaryRiskFactor] - Primary risk factor identified by AI
 * @returns {string} Tier key ('MINOR', 'MAJOR', or 'CAPEX')
 */
export function classifyJobTierWithTCS({ conversationText, tcs, suggestedTier, primaryRiskFactor } = {}) {
  // Prioritize TCS-based classification if available
  if (tcs !== undefined && suggestedTier) {
    console.log('[TierClassification] Using TCS-based classification:', {
      tcs,
      suggestedTier,
      primaryRiskFactor
    });

    // Apply TCS triage logic
    if (tcs > 80 && isStructuralHvacRoofing(primaryRiskFactor)) {
      return "CAPEX"; // High confidence + structural issues = CAPEX
    }
    
    if (tcs <= 80) {
      return "MAJOR"; // Ambiguity fallback
    }
    
    // For high confidence but non-critical issues, use AI suggestion
    if (tcs > 85 && suggestedTier === "MINOR") {
      return "MINOR";
    }
    
    // Default high confidence path
    return suggestedTier || "MAJOR";
  }
  
  // Fallback to keyword-based classification
  return classifyJobTier(conversationText);
}

/**
 * Check if risk factor indicates structural, HVAC, or roofing issues
 * @param {string} riskFactor - Primary risk factor
 * @returns {boolean} True if structural/HVAC/roofing
 */
function isStructuralHvacRoofing(riskFactor) {
  if (!riskFactor) return false;
  
  const criticalKeywords = [
    'structural', 'foundation', 'hvac', 'heating', 'cooling', 'air conditioning',
    'furnace', 'boiler', 'roof', 'roofing', 'load bearing', 'beam', 'joist',
    'electrical panel', 'main line', 'sewer', 'plumbing stack'
  ];
  
  const lowerRisk = riskFactor.toLowerCase();
  return criticalKeywords.some(keyword => lowerRisk.includes(keyword));
}

/**
 * Classify job tier based on conversation text (legacy method for backward compatibility)
 * @param {string} conversationText - Full conversation text
 * @returns {string} Tier key ('MINOR', 'MAJOR', or 'CAPEX')
 */
export function classifyJobTier(conversationText) {
  /** No text to score → default to major-system tier (3 credits), not small repair. */
  if (!conversationText) return "MAJOR";
  
  const text = conversationText.toLowerCase();

  const scatteredHandymanHits = [
    /\b(hang(?:ing)?|mount(?:ing)?)\b.{0,35}\b(picture|pictures|art|frame|mirror|shelf|tv)\b/i,
    /\b(picture\s+hanging|artwork)\b/i,
    /\b(faucet|drain|toilet|sink|shower|clog)\b/i,
    /\b(electric|outlet|switch|light|fixture|wiring)\b/i,
    /\b(door|window|drywall|patch|paint|touch[- ]?up)\b/i,
  ].filter((re) => re.test(text)).length;
  const coordinatedRoomRemodel =
    /\b(bathroom|kitchen)\s+remodel\b/i.test(text) ||
    /\bremodel\b.*\b(bathroom|kitchen)\b/i.test(text);
  if (scatteredHandymanHits >= 2 && !coordinatedRoomRemodel) {
    return "MINOR";
  }

  if (
    /\b(gut|entire\s+house|whole\s+house|full\s+house|full\s+home|unit\s+turn|addition|structural|major\s+renovation)\b/i.test(
      text
    )
  ) {
    return "CAPEX";
  }

  if (
    /\b(bedroom|guest\s+room|one\s+room|single\s+room)\b/i.test(text) &&
    /\bremodel\b/i.test(text)
  ) {
    return "MAJOR";
  }

  if (
    /\b(pantry|shelv|handyman|one[- ]day|small\s+job|fixture|patch|touch[- ]?up|dripping|leaky\s+faucet)\b/i.test(
      text
    ) &&
    !/\b(kitchen\s+remodel|bathroom\s+remodel|gut|whole\s+house)\b/i.test(text)
  ) {
    return "MINOR";
  }

  const scores = { MINOR: 0, MAJOR: 0, CAPEX: 0 };
  
  // Check each tier's keywords
  Object.entries(PROCUREMENT_TIERS).forEach(([tierKey, tier]) => {
    tier.keywords.forEach(keyword => {
      if (text.includes(keyword.toLowerCase())) {
        scores[tierKey] += 1;
        // Boost score for exact category matches
        if (tier.categories.some(cat => text.includes(cat.replace('_', ' ')))) {
          scores[tierKey] += 2;
        }
      }
    });
  });
  
  // Find tier with highest score
  const maxScore = Math.max(...Object.values(scores));
  if (maxScore === 0) return "MAJOR"; // Ambiguous / no keywords → middle fee

  let topTier = Object.entries(scores).find(([, score]) => score === maxScore)?.[0];

  // Lone "remodel" keyword should not auto-select CAPEX without gut/kitchen/bath/whole-home signals
  if (
    topTier === "CAPEX" &&
    scores.CAPEX <= 2 &&
    !/\b(kitchen|bathroom|gut|whole|entire|full\s+house|addition)\b/i.test(text)
  ) {
    topTier = scores.MAJOR >= scores.MINOR ? "MAJOR" : "MINOR";
  }

  return topTier || "MAJOR";
}

/**
 * Get tier information by key
 * @param {string} tierKey - Tier key ('MINOR', 'MAJOR', or 'CAPEX')
 * @returns {object} Tier information
 */
export function getTierInfo(tierKey) {
  return PROCUREMENT_TIERS[tierKey] || PROCUREMENT_TIERS.MINOR;
}

/**
 * Format tier fee for display
 * @param {string|object} tierKeyOrInfo - Tier key or tier info object
 * @returns {string} Formatted fee (e.g., "1 Credit")
 */
export function formatTierFee(tierKeyOrInfo) {
  const tier = typeof tierKeyOrInfo === 'string' 
    ? getTierInfo(tierKeyOrInfo) 
    : tierKeyOrInfo;
  return `$${tier.fee}`;
}